import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import "./DashboardCus.css";

const DashboardCus = () => {
  const [data, setData] = useState(null);
  const [customerSummary, setCustomerSummary] = useState([]);
  const [showModal] = useState(false);
  const [startDate, setStartDate] = useState(new Date(2025, 0));
  const [endDate, setEndDate] = useState(new Date(2025, 11));
  const navigate = useNavigate();

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const sorted = [...customerSummary].sort((a, b) => {
      const valA = a[key] ?? "";
      const valB = b[key] ?? "";

      if (typeof valA === "number" && typeof valB === "number") {
        return direction === "asc" ? valA - valB : valB - valA;
      }

      return direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    setCustomerSummary(sorted);
  };

  const loadData = useCallback(async () => {
    try {
      const startStr = `${startDate.getMonth() + 1}-${startDate.getFullYear()}`;
      const endStr = `${endDate.getMonth() + 1}-${endDate.getFullYear()}`;

      const res = await axios.get(
        `/dashboardcus?start=${startStr}&end=${endStr}`,
        { withCredentials: true }
      );

      setData(res.data);
    } catch (err) {
      console.error("Dashboard Error:", err);

      setData({
        summary: {
          total_tickets: 0,
          closed_within_sla: 0,
          violated_sla: 0,
          avg_star_year: 0,
        },
        tickets: [],
      });
    }
  }, [startDate, endDate]);

  const loadCustomerSummary = useCallback(async () => {
    try {
      const startStr = `${startDate.getMonth() + 1}-${startDate.getFullYear()}`;
      const endStr = `${endDate.getMonth() + 1}-${endDate.getFullYear()}`;

      const res = await axios.get(
        `/dashboardcus/all?start=${startStr}&end=${endStr}`,
        { withCredentials: true }
      );

      setCustomerSummary(res.data);
    } catch (err) {
      console.error("Dashboard List Error:", err);
      setCustomerSummary([]);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
    loadCustomerSummary();
  }, [loadData, loadCustomerSummary]);

  if (!data) return <div>Loading...</div>;

  const slaData = [
    { name: "Resolved on Time", value: data.summary.closed_within_sla },
    { name: "Resolved Late", value: data.summary.violated_sla },
  ];

  const COLORS = ["#22C55E", "#EF4444"];

  const severityData = data.tickets.reduce((acc, t) => {
    acc[t.severity_level] = (acc[t.severity_level] || 0) + 1;
    return acc;
  }, {});

  const severityChart = Object.keys(severityData).map((k) => ({
    name: k,
    value: severityData[k],
  }));

  const totalSLA = data.summary.closed_within_sla + data.summary.violated_sla;

  return (
    <div className="dash-container">
      <h2 className="title">Ticket Incident Dashboard</h2>

      {/* Filters */}
      <div className="filters">
        <div className="filter-item">
          <label>Start Month</label>
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            dateFormat="MM/yyyy"
            showMonthYearPicker
          />
        </div>

        <div className="filter-item">
          <label>End Month</label>
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            dateFormat="MM/yyyy"
            showMonthYearPicker
          />
        </div>

        <button
          className="apply-btn"
          onClick={() => {
            loadData();
            loadCustomerSummary();
          }}
        >
          Apply
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <h3>Total Tickets</h3>
          <p>{data.summary.total_tickets}</p>
        </div>

        <div className="summary-card green">
          <h3>Resolved on Time</h3>
          <p>{data.summary.closed_within_sla}</p>
        </div>

        <div className="summary-card red">
          <h3>Resolved Late</h3>
          <p>{data.summary.violated_sla}</p>
        </div>

        <div className="summary-card yellow">
          <h3>Avg Star</h3>
          <p>{Number(data.summary.avg_star_year).toFixed(2)}</p>
        </div>
        
      </div>

      {/* SLA + Severity Charts */}
      <div className="chart-row">
        <div className="chart-box">
          <h3>SLA Status</h3>

          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Tooltip
                formatter={(value, name) => [`${value} tickets`, name]}
              />
              <Pie
                dataKey="value"
                data={slaData}
                outerRadius={90}
                labelLine={false}
                label={({ name, value }) =>
                  totalSLA > 0
                    ? `${name} ${((value / totalSLA) * 100).toFixed(0)}%`
                    : ""
                }
              >
                {slaData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-box">
          <h3>Severity Distribution</h3>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={severityChart}>
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-section">
        <h3>Customer Incident Summary</h3>

        <table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort("company_name")}
                className="sortable"
              >
                Company{" "}
                {sortConfig.key === "company_name" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>

              <th
                onClick={() => handleSort("total_tickets")}
                className="sortable"
              >
                Total Tickets{" "}
                {sortConfig.key === "total_tickets" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>

              <th onClick={() => handleSort("sla_ok")} className="sortable">
                Resolved on Time{" "}
                {sortConfig.key === "sla_ok" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>

              <th
                onClick={() => handleSort("sla_violated")}
                className="sortable"
              >
                Resolved Late{" "}
                {sortConfig.key === "sla_violated" &&
                  (sortConfig.direction === "asc" ? "▲" : "▼")}
              </th>

              <th>More</th>
            </tr>
          </thead>

          <tbody>
            {customerSummary.map((c, i) => (
              <tr key={i}>
                <td>{c.company_name}</td>
                <td>{c.total_tickets}</td>
                <td className="sla-ok">{c.sla_ok ?? "-"}</td>
                <td className="sla-bad">{c.sla_violated ?? "-"}</td>

                <td>
                  <button
                    className="detail-btn"
                    onClick={() =>
                      navigate(`/admin/customer-tickets/${c.company_id}`, {
                        state: {
                          company_name: c.company_name,
                          start: startDate,
                          end: endDate,
                        },
                      })
                    }
                  >
                    View Tickets
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && <div className="modal-bg">Modal content</div>}
    </div>
  );
};

export default DashboardCus;
